"use client";

import Image from "next/image";
import React from "react";
import { ChevronRight, MapPin, Users } from "lucide-react";
import { DUMMY_IMAGES } from "@/data/whoWeAre";
import RichText from "@/utils/richText";
import { useRouter } from "next/navigation";

interface WhoWeAreProps {
  whoWeAreData: {
    images: string[];
    description: string;
  };
  stats: {
    total_packages: number;
    year_experience: number;
  };
}

const WhoWeAre = ({ whoWeAreData, stats }: WhoWeAreProps) => {
  const imagesArray =
    whoWeAreData?.images && whoWeAreData.images.length > 0
      ? whoWeAreData.images.slice(0, 3)
      : DUMMY_IMAGES.slice(0, 3);

  const router = useRouter();

  const cardStyles = [
    {
      initial: "z-10 -translate-x-20 translate-y-4 rotate-[-15deg]",
      hover:
        "group-hover:-translate-x-32 group-hover:-rotate-[-15deg] group-hover:-translate-y-4",
    },
    {
      initial: "z-30 translate-y-0 scale-105",
      hover: "group-hover:-translate-y-8 group-hover:scale-110",
    },
    {
      initial: "z-20 translate-x-24 translate-y-4 rotate-[8deg]",
      hover:
        "group-hover:translate-x-32 group-hover:rotate-[10deg] group-hover:-translate-y-2",
    },
  ];

  return (
    <section className="px-6 md:px-16 lg:px-24 py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-32 items-center">
        <div className="relative h-[450px] w-full max-w-[500px] mx-auto flex items-center justify-center group">
          {imagesArray.map((img, idx) => (
            <div
              key={idx}
              className={`
                absolute w-[220px] h-[320px] md:w-[260px] md:h-[360px] 
                overflow-hidden rounded-[2rem] shadow-2xl 
                transition-all duration-500 ease-out cursor-pointer
                hover:z-50 hover:scale-110
                ${cardStyles[idx].initial} 
                ${cardStyles[idx].hover}
              `}
            >
              <Image
                src={img}
                alt="Travel Experience"
                fill
                className="object-cover transition-transform duration-500 hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/5 hover:bg-transparent transition-colors duration-500" />
            </div>
          ))}
        </div>

        <div className="flex flex-col">
          <h2 className="text-primary-900 text-4xl md:text-5xl font-black leading-tight mb-8">
            Let’s Explore the World Together
          </h2>

          <RichText
            content={
              whoWeAreData?.description ||
              "Experience travel like never before with our curated global journeys."
            }
            className="text-gray-500 text-xl leading-relaxed mb-4 prose prose-p:mb-4"
          />

          <div className="flex items-center gap-12 mb-10">
            <div>
              <p className="text-4xl font-black text-primary-900 mb-1">
                {stats?.total_packages}+
              </p>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                Destinations
              </p>
            </div>
            <div className="w-px h-10 bg-gray-100" />
            <div>
              <p className="text-4xl font-black text-primary-900 mb-1">
                {stats?.year_experience}+
              </p>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                Years Experience
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="flex items-center gap-4 group/item">
              <div className="w-12 h-12 shrink-0 bg-primary-500 text-white rounded-full flex items-center justify-center group-hover/item:bg-primary-400 transition-colors duration-300">
                <MapPin size={22} />
              </div>
              <div>
                <h4 className="text-primary-500 font-bold text-lg mb-1">
                  Exclusive Trips
                </h4>
                <p className="text-gray-400 text-sm leading-snug">
                  Unique itineraries crafted for the curious soul.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 group/item">
              <div className="w-12 h-12 shrink-0 bg-primary-500 text-white rounded-full flex items-center justify-center group-hover/item:bg-primary-400 transition-colors duration-300">
                <Users size={22} />
              </div>
              <div>
                <h4 className="text-primary-500 font-bold text-lg mb-1">
                  Expert Guides
                </h4>
                <p className="text-gray-400 text-sm leading-snug">
                  Local storytellers who bring history to life.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => router.push("/package")}
            className="w-fit group/btn flex items-center gap-3 bg-tertiary-500 text-white px-8 py-4 rounded-full transition-all hover:bg-primary-500"
          >
            <span className="font-bold uppercase text-sm tracking-[0.2em]">
              Start Your Journey
            </span>
            <ChevronRight
              size={18}
              className="transition-transform group-hover/btn:translate-x-1"
            />
          </button>
        </div>
      </div>
    </section>
  );
};

export default WhoWeAre;
